import { Alert, Box, Button, Chip, Divider, Paper, Skeleton, Stack, Typography } from "@mui/material";
import type { ButtonProps, ChipProps } from "@mui/material";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { HStack, UserAvatar, colors } from "@zfp-akademie-a-s/zfp-design";
import { CalendarIcon, EditIcon, ExternalLinkIcon, LocationIcon, PhoneIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { IdCardIcon, MailIcon } from "Components/_Shared/Icons/Icons";
import { useClientDetail } from "Infrastructure/ApiZfp/AnalyzerApi";
import { RelatedClientDetailType } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  CLIENT_DETAIL_RETRY,
  ClientCardState,
  formatClientPhone,
  formatPersonalIdentificationNumber,
  getClientCardState,
  getLegalRepresentative,
  selectClientDetailClient,
} from "Infrastructure/Utils/ClientUtils";
import { type ReactNode, useContext } from "react";
import { CLIENT_CARD_TAG, formatBirthDateWithAge, formatCityAndPostalCode } from "./ClientCardDetail.helpers";
import { ClientCardLegalRepresentative } from "./ClientCardLegalRepresentative";
import { InfoItem } from "./InfoItem";

export type ClientCardRowAction = {
  title: string;
  icon?: ReactNode;
  tag?: {
    label: string;
    color: ChipProps["color"];
    variant: ChipProps["variant"];
  };
  hint?: string;
  button?: {
    label: string;
    onClick: () => void;
  } & Pick<ButtonProps, "disabled" | "loading" | "color" | "variant">;
};

type Props = {
  clientGuid: string;
  rows: ClientCardRowAction[];
  onClick?: () => void;
  legalRepresentationValid?: boolean;
};

const paperSx = {
  display: "flex",
  flexDirection: "column",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  overflow: "hidden",
} as const;

export const ClientCardDetail = ({ clientGuid, rows, onClick, legalRepresentationValid }: Props) => {
  const {
    data: client,
    isPending: isClientPending,
    isError: isClientError,
  } = useClientDetail(clientGuid, { query: { select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY } });
  const { readOnly } = useContext(AnalysisDetailContext);
  const formatLocale = useFormatLocale();
  const { t } = useResource();
  const isMinorClientEnabled = useFeatureFlag(FeatureFlag.MinorClient);

  if (isClientPending) {
    return (
      <Paper elevation={0} sx={paperSx}>
        <Box px={Spacing.XL} py={Spacing.L}>
          <HStack spacing={Spacing.L} alignItems="center" minWidth={200}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={140} height={20} />
              <Skeleton variant="text" width={100} height={16} />
            </Box>
          </HStack>
        </Box>
      </Paper>
    );
  }

  if (isClientError) {
    return <Alert severity="error">{t(Resources.Clients.ClientCard.Alerts.NotAvailable)}</Alert>;
  }

  if (client?.type === RelatedClientDetailType.related) {
    const fullName = getClientFullName({ name: client.firstName, surname: client.surname });
    const birthDateText = formatBirthDateWithAge(client.birthDate, formatLocale, t);
    return (
      <Paper elevation={0} sx={paperSx}>
        <Stack spacing={Spacing.XL} px={Spacing.XL} py={Spacing.XL} sx={{ backgroundColor: colors.backgroundLight }}>
          <HStack spacing={Spacing.L} alignItems="center">
            <UserAvatar firstName={client.firstName} lastName={client.surname} size="large" color="primary" />
            <Typography variant="body2" fontWeight={600}>
              {fullName}
            </Typography>
          </HStack>
          <HStack pl={Spacing.L}>
            <InfoItem icon={<CalendarIcon />} text={birthDateText} />
          </HStack>
        </Stack>
        {rows.map((row, index) => (
          <Box key={`${index}-${row.title}`}>
            <Divider />
            <HStack px={Spacing.XL} py={Spacing.XL} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={Spacing.M}>
              <HStack spacing={Spacing.M} alignItems="center">
                {row.icon && (
                  <Box color="text.secondary" display="flex" alignItems="center" sx={{ "& svg": { fontSize: 18 } }}>
                    {row.icon}
                  </Box>
                )}
                <Typography variant="body2" fontWeight={500}>
                  {row.title}
                </Typography>
              </HStack>
              <HStack spacing={Spacing.M} alignItems="center">
                {row.tag && <Chip size="small" label={row.tag.label} color={row.tag.color} variant={row.tag.variant} />}
                {row.button && (
                  <Button
                    onClick={row.button.onClick}
                    disabled={row.button.disabled}
                    loading={row.button.loading}
                    color={row.button.color}
                    variant={row.button.variant ?? "contained"}
                  >
                    {row.button.label}
                  </Button>
                )}
              </HStack>
            </HStack>
            {row.hint && (
              <Box px={Spacing.XL} pb={Spacing.XL}>
                <Alert severity="info">{row.hint}</Alert>
              </Box>
            )}
          </Box>
        ))}
      </Paper>
    );
  }

  const fullName =
    getClientFullName({
      titlePrefix: client.titlePrefix,
      name: client.firstName,
      surname: client.surname,
      titlePostfix: client.titlePostfix,
    }).trim() || "—";

  const phone = formatClientPhone(client.phonePrefix, client.phoneNationalNumber);

  const birthDateText = formatBirthDateWithAge(client.birthDate, formatLocale, t);

  const address = formatCityAndPostalCode(client.address);

  const mangoUpCardUrl = "mangoUpCardUrl" in client ? client.mangoUpCardUrl : undefined;

  const legalRepresentative = isMinorClientEnabled ? getLegalRepresentative(client) : undefined;
  // The client detail carries no minor flag - a legal representation that is still valid is what marks a minor
  const isMinor = !!legalRepresentative && legalRepresentationValid === true;

  const cardState = getClientCardState(client);
  const cardTag = CLIENT_CARD_TAG[cardState];

  // A Mango client's data is maintained in MangoUp, so there is nothing editable here - the MangoUp button is the way in
  const isEditable = cardState === ClientCardState.NotInMango;

  const defaultRows: ClientCardRowAction[] = [
    {
      title: t(Resources.Clients.ClientCard.Title),
      tag: { label: t(cardTag.label), color: cardTag.color, variant: "filled" },
      hint: cardState === ClientCardState.Missing ? t(Resources.Clients.ClientCard.MissingHint) : undefined,
    },
  ];

  const allRows = [...defaultRows, ...rows];

  return (
    <Paper elevation={0} sx={paperSx}>
      <Stack px={Spacing.XL} py={Spacing.XL} sx={{ backgroundColor: colors.backgroundLight }}>
        <HStack alignItems="flex-start" justifyContent="space-between" gap={Spacing.L} flexWrap="wrap">
          <Box flexShrink={0}>
            <UserAvatar firstName={client.firstName} lastName={client.surname} size="medium" color="primary" />
          </Box>

          <Stack spacing={Spacing.L} flex={1} pt={Spacing.M}>
            <HStack spacing={Spacing.M} alignItems="center">
              <Typography variant="body1" fontWeight={600}>
                {fullName}
              </Typography>
              {isMinor && <Chip size="small" color="primary" label={t(Resources.Clients.ClientCard.Minor)} sx={{ flexShrink: 0 }} />}
            </HStack>
          </Stack>

          <HStack spacing={Spacing.M} alignItems="center" pt={Spacing.XS}>
            {mangoUpCardUrl && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => window.open(mangoUpCardUrl, "_blank", "noopener,noreferrer")}
                endIcon={<ExternalLinkIcon fontSize="small" />}
              >
                MangoUp
              </Button>
            )}
            {onClick && !readOnly && isEditable && (
              <Button variant="contained" size="small" color="secondary" onClick={onClick} endIcon={<EditIcon fontSize="small" />}>
                {t(Resources.Common.Edit)}
              </Button>
            )}
          </HStack>
        </HStack>

        <HStack spacing={Spacing.XXXL} flexWrap="wrap" pl={Spacing.XXXL}>
          <Stack spacing={Spacing.M} pl={Spacing.XL}>
            <InfoItem icon={<CalendarIcon />} text={birthDateText} />
            <InfoItem icon={<IdCardIcon />} text={formatPersonalIdentificationNumber(client.personalIdentificationNumber)} />
            <InfoItem icon={<MailIcon />} text={"email" in client ? client.email : undefined} />
          </Stack>
          <Stack spacing={Spacing.M}>
            <InfoItem icon={<PhoneIcon />} text={phone} />
            <InfoItem icon={<LocationIcon />} text={address} />
          </Stack>
        </HStack>
      </Stack>

      {legalRepresentative && (
        <ClientCardLegalRepresentative representative={legalRepresentative} showAdultWarning={legalRepresentationValid === false} />
      )}

      {allRows.map((row, index) => (
        <Box key={`${index}-${row.title}`}>
          <Divider />
          <HStack px={Spacing.XL} py={Spacing.XL} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={Spacing.M}>
            <HStack spacing={Spacing.M} alignItems="center">
              {row.icon && (
                <Box color="text.secondary" display="flex" alignItems="center" sx={{ "& svg": { fontSize: 18 } }}>
                  {row.icon}
                </Box>
              )}
              <Typography variant="body2" fontWeight={500}>
                {row.title}
              </Typography>
            </HStack>
            <HStack spacing={Spacing.M} alignItems="center">
              {row.tag && <Chip size="small" label={row.tag.label} color={row.tag.color} variant={row.tag.variant} />}
              {row.button && (
                <Button
                  onClick={row.button.onClick}
                  disabled={row.button.disabled}
                  loading={row.button.loading}
                  color={row.button.color}
                  variant={row.button.variant ?? "contained"}
                >
                  {row.button.label}
                </Button>
              )}
            </HStack>
          </HStack>
          {row.hint && (
            <Box px={Spacing.XL} pb={Spacing.XL}>
              <Alert severity="info">{row.hint}</Alert>
            </Box>
          )}
        </Box>
      ))}
    </Paper>
  );
};
